import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  useTheme,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { red } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { formatString } from "./Utility/Format";
import {
  DETAIL_TAB,
  PollingContext,
  ProcessResult,
  ProjectDetailContext,
} from "../pages/ProjectDetail";
import { useContext } from "react";
import CoordinateContext from "./ProjectDetail/Contexts/CoordinateContext";

interface Props {
  open: boolean;
  processResult: ProcessResult;
  handleClickClose: () => void;
}

export default function ProcessCompletedDialog({
  open,
  processResult,
  handleClickClose,
}: Props) {
  const { t } = useTranslation();
  const isSuccess = processResult.result === "success";
  const isHelmertSuccess =
    processResult.result === "success" && processResult.process === "helmert";
  const theme = useTheme();
  const getProcessResult = (process: string) => {
    switch (process) {
      case "helmert":
        return t("COORDINATE_TRANSFOR");
      case "point removal":
        return t("REMOVE_POINT_GROUP");
      case "transformation":
        return t("COORDINATE_PROCESS");
    }
  };

  const pleaseContactUsMessage = t("PLEASE_CONTACT_US");
  const [before, after] = pleaseContactUsMessage.split("%s");

  return (
    <Dialog
      open={open}
      PaperProps={{
        style: {
          background: theme.palette.base.modal,
          boxShadow: "none",
          border: "1px solid #44444F",
          paddingBottom: "16px",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.base.dialogBackground,
          },
        },
      }}
    >
      <DialogTitle>
        <div>
          {isSuccess ? t("PROCESSING_COMPLETE") : t("PROCESSING_ERROR")}
        </div>
        <div className="border-comm" />
      </DialogTitle>
      <Button
        sx={{
          fontSize: "18px",
          position: "absolute",
          margin: "0px",
          padding: "0px",
          right: 24,
          top: 15,
          color: theme.palette.base.contrast,
        }}
        onClick={handleClickClose}
      >
        ×
      </Button>
      <DialogContent>
        <div style={{ textAlign: "center" }}>
          {isSuccess && (
            <div style={{ color: "black" }}>
              <div
                style={{
                  margin: 0,
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleIcon color="success" />
                <DialogContentText
                  style={{ color: theme.palette.base.contrast }}
                >
                  {formatString(
                    t("COMPLETED"),
                    getProcessResult(processResult.process)
                  )}
                </DialogContentText>
              </div>
              <DialogContentText style={{ color: theme.palette.base.contrast }}>
                {t("WANT_TO_UPDATE_POINT")}
              </DialogContentText>
            </div>
          )}
          {!isSuccess && (
            <div style={{ color: "black" }}>
              <DialogContentText style={{ color: theme.palette.base.contrast }}>
                {formatString(
                  t("FAILED"),
                  getProcessResult(processResult.process)
                )}
              </DialogContentText>
              <div
                style={{
                  margin: 0,
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WarningAmberIcon sx={{ color: red[500] }} />
                <DialogContentText
                  style={{ color: theme.palette.base.contrast }}
                >{`(${t("PROCESS_ID")}:${
                  processResult.id
                })`}</DialogContentText>
              </div>
              <div style={{ marginTop: "16px" }}>
                <DialogContentText
                  style={{ color: theme.palette.base.contrast }}
                >
                  {before}
                  <Link
                    href="https://sc-quick3d.zendesk.com/hc/ja"
                    target="_blank"
                  >
                    {t("SUPPORT_CENTER")}
                  </Link>
                  {after}
                </DialogContentText>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          mx: "24px",
          width: "450px",
        }}
      >
        {isSuccess && (
          <SuccessButtons
            handleClickClose={handleClickClose}
            isHelmertSuccess={isHelmertSuccess}
          />
        )}
        {!isSuccess && <ErrorButton handleClickClose={handleClickClose} />}
      </DialogActions>
    </Dialog>
  );
}

const SuccessButtons = ({
  handleClickClose,
  isHelmertSuccess,
}: {
  handleClickClose: () => void;
  isHelmertSuccess: boolean;
}) => {
  const { callback } = useContext(PollingContext);
  const { projectDetail, reloadPointCloud, getProjectDetail, setDetailTab } =
    useContext(ProjectDetailContext);
  const { fetchCS } = useContext(CoordinateContext);
  const { t } = useTranslation();

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClickClose}
        color="inherit"
        sx={{
          width: "185px",
          borderRadius: 0,
        }}
      >
        {t("CANCEL")}
      </Button>
      <Button
        onClick={() => {
          reloadPointCloud();
          getProjectDetail();
          fetchCS(projectDetail?.id);
          handleClickClose();

          // helmert_successであればtab切り替え
          if (isHelmertSuccess) {
            setDetailTab((prev) => {
              return prev === DETAIL_TAB.PHOTO
                ? DETAIL_TAB.PHOTO
                : DETAIL_TAB.HELMERT;
            });
          }

          // RemoTabPanelマウント時はgetTimeStampがsetされている
          callback();
        }}
        variant="contained"
        sx={{
          width: "185px",
          borderRadius: 0,
        }}
      >
        {t("UPDATE")}
      </Button>
    </>
  );
};

const ErrorButton = ({
  handleClickClose,
}: {
  handleClickClose: () => void;
}) => {
  const { getProjectDetail } = useContext(ProjectDetailContext);
  const { t } = useTranslation();

  return (
    <Button
      variant="contained"
      onClick={() => {
        getProjectDetail();
        handleClickClose();
      }}
      sx={{
        width: "185px",
        borderRadius: 0,
      }}
    >
      {t("CONFIRMATION")}
    </Button>
  );
};
